import { SwipeableReviewCard } from 'app/components/swipeable-review-card';
import { ActivityIndicator, ScrollView, StyleSheet, View } from 'react-native';
import { HubCard, HubCopy, HubEmoji, ItemFace, QuickOffersOverview, hubColors, styles } from '../components';
import { OfferHubCompletion } from './offer-hub-completion';
import { OfferHubTutorial } from './offer-hub-tutorial';

import type { OfferHubState } from '../hooks';

export function OfferHubStage({
  swiping,
  tutorialVisible,
  swipeTutorialSwiping,
  loaded,
  t,
  replayingSwipeTutorial,
  swipeTutorialStep,
  swipeTutorialCard,
  tab,
  focused,
  setSwipeTutorialStep,
  currentItem,
  completed,
  accepted,
  navigation,
  statistics,
  refreshStatistics,
  pending,
  items,
  swipeCard,
  showError,
  decide,
  theme,
  inProgress,
  round,
  overviewActive,
}: Pick<
  OfferHubState,
  | 'swiping'
  | 'tutorialVisible'
  | 'swipeTutorialSwiping'
  | 'loaded'
  | 't'
  | 'replayingSwipeTutorial'
  | 'swipeTutorialStep'
  | 'swipeTutorialCard'
  | 'tab'
  | 'focused'
  | 'setSwipeTutorialStep'
  | 'currentItem'
  | 'completed'
  | 'accepted'
  | 'navigation'
  | 'statistics'
  | 'refreshStatistics'
  | 'pending'
  | 'items'
  | 'swipeCard'
  | 'showError'
  | 'decide'
  | 'theme'
  | 'inProgress'
  | 'round'
  | 'overviewActive'
>) {
  return (
    <ScrollView
      style={styles.flex}
      contentContainerStyle={[styles.content, { flexGrow: 1, flex: undefined, minHeight: 380 }]}
      scrollEnabled={!swiping && !tutorialVisible && !swipeTutorialSwiping}
      showsVerticalScrollIndicator={false}
    >
      {!loaded ? (
        <View style={styles.center}>
          <ActivityIndicator color={hubColors.teal} accessibilityLabel={t('loading')} />
        </View>
      ) : replayingSwipeTutorial ? (
        <OfferHubTutorial
          swipeTutorialStep={swipeTutorialStep}
          swipeTutorialCard={swipeTutorialCard}
          t={t}
          tab={tab}
          focused={focused}
          setSwipeTutorialStep={setSwipeTutorialStep}
          currentItem={currentItem}
        />
      ) : completed ? (
        <OfferHubCompletion
          t={t}
          accepted={accepted}
          navigation={navigation}
          statistics={statistics}
          refreshStatistics={refreshStatistics}
          pending={pending}
        />
      ) : swiping && currentItem ? (
        <View style={styles.flex}>
          {/* Stable keyed layers let the next card become active without remounting its image. */}
          {items
            .slice(0, 2)
            .reverse()
            .map((item) => {
              const isCurrent = item.id === currentItem.id;
              return (
                <View
                  key={item.id}
                  pointerEvents={isCurrent ? 'auto' : 'none'}
                  accessibilityElementsHidden={!isCurrent}
                  aria-hidden={!isCurrent}
                  importantForAccessibility={isCurrent ? 'auto' : 'no-hide-descendants'}
                  style={StyleSheet.absoluteFill}
                >
                  <SwipeableReviewCard
                    ref={isCurrent ? swipeCard : undefined}
                    resetKey={item.id}
                    acceptLabel={t('accept')}
                    rejectLabel={t('reject')}
                    canAccept={isCurrent}
                    canReject={isCurrent}
                    disabled={!isCurrent || showError || !focused}
                    onAccept={() => {
                      if (isCurrent) decide('accept');
                    }}
                    onReject={() => {
                      if (isCurrent) decide('reject');
                    }}
                    showOverlays={isCurrent}
                    overlayVariant="gradient"
                    maxRotation={6}
                    verticalDragFactor={0.08}
                    style={[
                      styles.flex,
                      styles.card,
                      { padding: 0, overflow: 'hidden', borderColor: theme.colors['border.secondary'] },
                    ]}
                  >
                    <ItemFace item={item} announce={isCurrent} />
                  </SwipeableReviewCard>
                </View>
              );
            })}
        </View>
      ) : currentItem ? (
        <QuickOffersOverview
          items={items.slice(0, 3)}
          remaining={inProgress ? items.length : undefined}
          total={inProgress ? round?.items.length : undefined}
          active={overviewActive}
        />
      ) : (
        <HubCard>
          <HubEmoji>👏</HubEmoji>
          <HubCopy title={t('caughtUp')} description={t('caughtUpDescription')} />
        </HubCard>
      )}
    </ScrollView>
  );
}
